import * as ar from './markets/ar';
import * as au from './markets/au';
import * as be from './markets/be';
import * as br from './markets/br';
import * as ca from './markets/ca';
import * as ch from './markets/ch';
import * as de from './markets/de';
import * as dk from './markets/dk';
import * as es from './markets/es';
import * as fi from './markets/fi';
import * as fr from './markets/fr';
import * as gb from './markets/gb';
import * as gr from './markets/gr';
import * as ie from './markets/ie';
import * as india from './markets/in';
import * as italy from './markets/it';
import * as jp from './markets/jp';
import * as kr from './markets/kr';
import * as lu from './markets/lu';
import * as me from './markets/me';
import * as mx from './markets/mx';
import * as nl from './markets/nl';
import * as no from './markets/no';
import * as pl from './markets/pl';
import * as se from './markets/se';
import * as sg from './markets/sg';
import * as sm from './markets/sm';
import * as sm_with_wltp from './markets/smwltp';
import * as tr from './markets/tr';
import * as tw from './markets/tw';
import * as us from './markets/us';
import * as za from './markets/za';

import DefaultMarket from '~/app/market-config/markets/default/initial-market-conf';
import DefaultMarketUI from '~/app/market-config/markets/default/market-ui-conf';
import { AemMarketConfig } from '~/app/market-config/models/aem-market-config';
import {
	CarConfiMarketConfiguration,
	PartialCarConfiguration,
} from '~/app/market-config/models/car-confi-market-configuration';
import { CarConfiUiConfiguration, PartialUiConfiguration } from '~/app/market-config/models/car-confi-ui-configuration';
import { MarketIdentifier } from '~/app/market-config/models/market-identifier';

type MarketConfig = Record<string, PartialCarConfiguration | PartialUiConfiguration>;

const LOCAL_MARKET_CONFIGURATION = new Map<MarketIdentifier, MarketConfig>([
	['AR', ar as MarketConfig],
	['AU', au as MarketConfig],
	['BE', be as MarketConfig],
	['CA', ca as MarketConfig],
	['CH', ch as MarketConfig],
	['DE', de as MarketConfig],
	['DK', dk as MarketConfig],
	['ES', es as MarketConfig],
	['FI', fi as MarketConfig],
	['FR', fr as MarketConfig],
	['GB', gb as MarketConfig],
	['GR', gr as MarketConfig],
	['IE', ie as MarketConfig],
	['IN', india as MarketConfig],
	['IT', italy as MarketConfig],
	['LU', lu as MarketConfig],
	['NL', nl as MarketConfig],
	['NO', no as MarketConfig],
	['PL', pl as MarketConfig],
	['SE', se as MarketConfig],
	['SG', sg as MarketConfig],
	['TR', tr as MarketConfig],
	['SM', sm as MarketConfig],
	['US', us as MarketConfig],
	['BR', br as MarketConfig],
	['JP', jp as MarketConfig],
	['KR', kr as MarketConfig],
	['ME', me as MarketConfig],
	['MX', mx as MarketConfig],
	['TW', tw as MarketConfig],
	['ZA', za as MarketConfig],
	['AZ', me as MarketConfig],
	['BH', me as MarketConfig],
	['DU', me as MarketConfig],
	['SA', me as MarketConfig],
	['KW', me as MarketConfig],
	['OM', me as MarketConfig],
	['QA', me as MarketConfig],
	['JO', me as MarketConfig],
	['LB', me as MarketConfig],
	['EE', sm_with_wltp as MarketConfig],
	['LV', sm_with_wltp as MarketConfig],
	['LT', sm_with_wltp as MarketConfig],
]);

const loadConfig = <T>(
	config: Map<MarketIdentifier, Record<'local' | string, T>>,
	marketId: MarketIdentifier,
	key: string,
): T => {
	const marketConfigurations = config.get(marketId);
	if (!marketConfigurations) {
		return {} as T;
	}

	if (!marketConfigurations[key]) {
		return {} as T;
	}
	const value = marketConfigurations[key];
	return value;
};

const cleanupCarlineSpecialChars = (carlineId: string): string => {
	// this needs to be done for all carlines that have special characters like - etc and will be replaced with a _
	return carlineId.replace(/(-)/g, '_');
};

const mapCmsConfigToNGCMarketConfig = <T extends object>(cmsData: Partial<T>, defaultConfig: T): Partial<T> => {
	return (
		Object.keys(cmsData)
			.map((key) => {
				if (!Object.keys(defaultConfig).find((configKey) => configKey === key)) return;
				return cmsData[key as keyof T] !== undefined
					? ({ [key]: cmsData[key as keyof T] } as Partial<T>)
					: undefined;
			})
			.filter((item) => item)
			.reduce((accumulator, cmsSetting) => ({ ...accumulator, ...cmsSetting }), {} as Partial<T>) ?? {}
	);
};

export const loadMarketConfig = (aemConfig: AemMarketConfig): CarConfiMarketConfiguration => {
	const { carlineId, market } = aemConfig;
	const defaultConfig = DefaultMarket(carlineId);
	const migratedCmsConfig: PartialCarConfiguration = { ...aemConfig, nextBestActions: aemConfig.nbas };
	const cmsConfig = mapCmsConfigToNGCMarketConfig(migratedCmsConfig, defaultConfig);
	const carlineIdKey = cleanupCarlineSpecialChars(carlineId);

	const smallMarketCheck = LOCAL_MARKET_CONFIGURATION.has(market.toUpperCase() as MarketIdentifier);
	const definedMarket = (smallMarketCheck ? market.toUpperCase() : 'SM') as MarketIdentifier;
	const etronConfig = carlineId.includes('etron')
		? loadConfig(LOCAL_MARKET_CONFIGURATION, definedMarket, 'etron')
		: {};

	return {
		...defaultConfig,
		...loadConfig(LOCAL_MARKET_CONFIGURATION, definedMarket, 'local'),
		...etronConfig,
		...loadConfig(LOCAL_MARKET_CONFIGURATION, definedMarket, carlineIdKey),
		...cmsConfig,
	};
};

export const loadMarketUiConfig = (aemConfig: AemMarketConfig): CarConfiUiConfiguration => {
	const { carlineId, market } = aemConfig;
	const defaultUiConfig = DefaultMarketUI();

	const cmsConfig = mapCmsConfigToNGCMarketConfig(aemConfig, defaultUiConfig);
	const carlineIdKey = cleanupCarlineSpecialChars(carlineId);
	const smallMarketCheck = LOCAL_MARKET_CONFIGURATION.has(market.toUpperCase() as MarketIdentifier);
	const definedMarket = (smallMarketCheck ? market.toUpperCase() : 'SM') as MarketIdentifier;

	const marketConfig = loadConfig(LOCAL_MARKET_CONFIGURATION, definedMarket, 'local_ui') as PartialUiConfiguration;
	const carlineConfig = loadConfig(
		LOCAL_MARKET_CONFIGURATION,
		definedMarket,
		`${carlineIdKey}_ui`,
	) as PartialUiConfiguration;

	return {
		...defaultUiConfig,
		...marketConfig,
		...carlineConfig,
		...cmsConfig,
		options: {
			...defaultUiConfig.options,
			...marketConfig.options,
			...carlineConfig.options,
		},
	};
};
